import React, { useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, Webhook, Loader2, Zap } from 'lucide-react';
import { toast } from 'sonner';

/** iter55g43 — Stripe Connect webhook signing secret (rotate-in-app).
 *
 *  Distinct from the main `stripe_webhook_secret` because Connect
 *  webhooks fire on the platform's connected-accounts stream and
 *  have their own signing key (Stripe Dashboard → Webhooks → the
 *  endpoint with "Events on Connected accounts" enabled).
 *
 *  The backend endpoint `/api/webhook/stripe/connect` prefers this
 *  DB-stored value, falling back to the `STRIPE_CONNECT_WEBHOOK_SECRET`
 *  env var for backward compat with existing deployments.
 */
export default function StripeConnectWebhookConfig({ cfg, onSaved }) {
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);

  const configured = Boolean(cfg?.stripe_connect_webhook_secret_configured);

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.saveConfig({ stripe_connect_webhook_secret: secret });
      toast.success(secret ? 'Stripe Connect webhook secret saved.' : 'Stripe Connect webhook secret cleared.');
      setSecret('');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save Connect webhook secret.');
    } finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await adminApi.saveConfig({ stripe_connect_webhook_secret: '' });
      toast.success('Stripe Connect webhook secret cleared.');
      setSecret('');
      onSaved?.();
    } catch { toast.error('Clear failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rounded-md border border-border bg-card p-4" data-testid="admin-stripe-connect-webhook">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 flex h-4 w-4 items-center justify-center">
          <Webhook className="h-4 w-4 text-primary" />
          <Zap className="-ml-1 h-2.5 w-2.5 text-amber-500" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <div className="text-[12.5px] font-semibold">Stripe Connect Webhook Signing Secret</div>
          <div className="text-[11px] text-muted-foreground">
            Verifies <code>/api/webhook/stripe/connect</code> events on the platform's connected-accounts stream. Get this from{' '}
            <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">dashboard.stripe.com/webhooks</a>
            {' '}— the endpoint with <strong>"Events on Connected accounts"</strong> enabled. Starts with <code>whsec_…</code>.
            {' '}
            {configured
              ? <span className="text-success">Currently configured</span>
              : <span className="text-warning-foreground">Not configured — Connect webhook falls back to the <code>STRIPE_CONNECT_WEBHOOK_SECRET</code> env var if present.</span>}
          </div>
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <div className="flex-1 max-w-md">
          <Label className="text-[11px]">Connect webhook secret</Label>
          <Input
            type="password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder={configured ? 'whsec_… (replace current)' : 'whsec_xxxxxxxxxxxxx'}
            className="mt-1 h-8 font-mono-stat text-[11.5px]"
            data-testid="admin-stripe-connect-webhook-input"
          />
        </div>
        <div className="flex items-end gap-2">
          <Button
            size="sm"
            onClick={save}
            disabled={busy || !secret}
            className="h-8 gap-1.5"
            data-testid="admin-stripe-connect-webhook-save"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save
          </Button>
          {configured && (
            <Button
              size="sm"
              variant="ghost"
              onClick={clear}
              disabled={busy}
              className="h-8 gap-1.5 text-muted-foreground"
              data-testid="admin-stripe-connect-webhook-clear"
            >
              Clear
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
